import { Component, type ErrorInfo, type ReactNode } from "react";
import { clearChunkRecovery, recoverStaleBundle } from "../lib/chunkRecovery";

interface AppErrorBoundaryState {
  error: Error | null;
  refreshing: boolean;
}

export class AppErrorBoundary extends Component<{ children: ReactNode }, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = { error: null, refreshing: false };

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return { error, refreshing: false };
  }

  componentDidCatch(error: Error, _errorInfo: ErrorInfo): void {
    const refreshing = recoverStaleBundle(error, {
      storage: window.sessionStorage,
      path: window.location.pathname,
      reload: () => window.location.reload(),
    });
    if (refreshing) this.setState({ refreshing: true });
  }

  private retry = () => {
    clearChunkRecovery(window.sessionStorage);
    window.location.reload();
  };

  render() {
    if (this.state.refreshing) {
      return (
        <div className="full-page-message">
          Updating the app. Reopen Capture after reload to check any in-progress draft.
        </div>
      );
    }

    if (this.state.error) {
      return (
        <div className="full-page-message full-page-message--card" role="alert">
          <h1>The app could not finish loading</h1>
          <p>Reload the app, then reopen Capture to check any in-progress draft.</p>
          <button type="button" className="button button--primary" onClick={this.retry}>
            Reload the app
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
